import { ListingHistoryState } from '__generated__/athena/graphql';
import type { MarketplaceProperty } from 'components/marketplace/property/types';

type ListingHistoryEvent = MarketplaceProperty['listingHistory'][number];

export type PricingEvent = ListingHistoryEvent & { displayDate: string };

const formatEventDate = (timestamp: string) =>
  new Date(timestamp).toLocaleDateString('en-us', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

// Non-MLS pending events do not represent a public price change.
export const getPricingEvents = (
  listingHistory: MarketplaceProperty['listingHistory'],
): PricingEvent[] => {
  const eventsByDate = listingHistory.reduce<Record<string, PricingEvent>>((map, event) => {
    if (!event.timestamp) {
      return map;
    }

    const displayDate = formatEventDate(event.timestamp);
    if (!map[displayDate]) {
      map[displayDate] = { ...event, displayDate };
    }

    return map;
  }, {});

  return Object.values(eventsByDate)
    .filter((event) => event.state !== ListingHistoryState.SALE_PENDING || event.mls)
    .filter(
      (event) =>
        event.soldPriceCents || event.currentListPriceCents || event.originalListPriceCents,
    );
};

const ACTIVE_LISTING_STATES: ReadonlySet<ListingHistoryState> = new Set([
  ListingHistoryState.FOR_SALE,
  ListingHistoryState.COMING_SOON,
]);

export type PriceCut = {
  originalListPriceCents: number;
  currentListPriceCents: number;
  cutCents: number;
  cutDisplayDate: string | null;
};

// Events arrive in reverse chronological order; the newest active event
// carries the listing period's original price alongside the current one.
export const getPriceCut = (
  listingHistory: MarketplaceProperty['listingHistory'],
): PriceCut | null => {
  const events = getPricingEvents(listingHistory);
  const [latest] = events;
  if (!latest || !ACTIVE_LISTING_STATES.has(latest.state)) {
    return null;
  }

  const currentListPriceCents = latest.currentListPriceCents;
  const originalListPriceCents = latest.originalListPriceCents;
  if (
    currentListPriceCents == null ||
    originalListPriceCents == null ||
    originalListPriceCents <= currentListPriceCents
  ) {
    return null;
  }

  let cutDisplayDate: string | null = null;
  for (let index = 0; index < events.length - 1; index += 1) {
    const newer = events[index];
    const older = events[index + 1];
    if (!newer || !older || !ACTIVE_LISTING_STATES.has(older.state)) {
      break;
    }
    if (
      newer.currentListPriceCents != null &&
      older.currentListPriceCents != null &&
      newer.currentListPriceCents < older.currentListPriceCents
    ) {
      cutDisplayDate = newer.displayDate;
      break;
    }
  }

  return {
    originalListPriceCents,
    currentListPriceCents,
    cutCents: originalListPriceCents - currentListPriceCents,
    cutDisplayDate,
  };
};

export type MonthlyCostInput = Pick<
  MarketplaceProperty,
  'estimatedMonthlyPayment' | 'annualPropertyTax' | 'hoaMonthlyDues'
>;

export type MonthlyCost = {
  principalAndInterest: number;
  monthlyTax: number | null;
  hoa: number | null;
  total: number;
};

// Dollars in, dollars out. Insurance is absent because no field carries it, so
// this total is below a buyer's real monthly cost.
export const getAllInMonthlyCost = ({
  estimatedMonthlyPayment,
  annualPropertyTax,
  hoaMonthlyDues,
}: MonthlyCostInput): MonthlyCost | null => {
  if (estimatedMonthlyPayment == null) {
    return null;
  }

  const monthlyTax = annualPropertyTax == null ? null : Math.round(annualPropertyTax / 12);
  const hoa = hoaMonthlyDues ?? null;

  return {
    principalAndInterest: estimatedMonthlyPayment,
    monthlyTax,
    hoa,
    total: estimatedMonthlyPayment + (monthlyTax ?? 0) + (hoa ?? 0),
  };
};

// Mirrors the Buy Direct checkout's earliest selectable close-of-escrow date
// (EARLIEST_CLOSE_OF_ESCROW_DAYS in buyDirectCheckout/steps/ScheduleAndLogistics).
const CLOSE_WINDOW_DAYS = 21;

export const getKeysByDate = (now: Date = new Date()): Date => {
  const keysBy = new Date(now);
  keysBy.setDate(keysBy.getDate() + CLOSE_WINDOW_DAYS);
  return keysBy;
};

export const formatKeysByDate = (date: Date): string =>
  `${date.getDate()} ${date.toLocaleDateString('en-us', { month: 'long' })}`;

// Raw day counts read as staleness on long-DOM homes, so freshness is shown
// only as a tag and only within this window.
const RECENTLY_LISTED_MAX_DAYS = 30;

export const isRecentlyListed = (daysOnMarket: number | null): boolean =>
  daysOnMarket != null && daysOnMarket >= 0 && daysOnMarket <= RECENTLY_LISTED_MAX_DAYS;
